import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  BarChart3,
  LineChart,
  Activity,
  Users,
  Key,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Network,
  Flame
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import api from '@/services/api';
import { apiProviderService } from '@/services/apiProviderService';
import Pagination from '@/components/ui/Pagination';

interface DashboardOverview {
  totalApiKeys: number;
  activeApiKeys: number;
  totalProviders: number;
  activeProviders: number;
  totalEndpoints: number;
  activeEndpoints: number;
  totalRequests24h: number;
  totalRequests7d: number;
  totalRequests30d: number;
  totalRequestsAllTime: number;
  successRate: number;
  avgResponseTime: number;
}

interface RecentActivity {
  id: string;
  endpoint: string;
  method: string;
  statusCode: number;
  responseTime: number;
  apiKeyName: string;
  createdAt: string;
}

interface AnalyticsData {
  period: string;
  requestData: Array<{
    date: string;
    count: number;
  }>;
  statusCodeDistribution: Array<{
    statusCode: number;
    count: number;
  }>;
  methodDistribution: Array<{
    method: string;
    count: number;
  }>;
  responseTimeStats: {
    average: number;
    minimum: number;
    maximum: number;
  };
}

interface TopApiKey {
  id: string;
  name: string;
  description?: string;
  requestCount: number;
}

interface TopProvider {
  id: string;
  name: string;
  baseUrl?: string;
  description?: string;
  hitCount: number;
}

interface TopEndpoint {
  providerId: string;
  providerName: string;
  endpoint: string;
  method: string;
  hitCount: number;
}

interface DashboardData {
  overview: DashboardOverview;
  recentActivity: RecentActivity[];
  topApiKeys: TopApiKey[];
  topProviders: TopProvider[];
  topEndpoints: TopEndpoint[];
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
    },
  },
};

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Loading dashboard...');
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    overview: {
      totalApiKeys: 0,
      activeApiKeys: 0,
      totalProviders: 0,
      activeProviders: 0,
      totalEndpoints: 0,
      activeEndpoints: 0,
      totalRequests24h: 0,
      totalRequests7d: 0,
      totalRequests30d: 0,
      totalRequestsAllTime: 0,
      successRate: 100,
      avgResponseTime: 0
    },
    recentActivity: [],
    topApiKeys: [],
    topProviders: [],
    topEndpoints: []
  });
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    period: '30d',
    requestData: [],
    statusCodeDistribution: [],
    methodDistribution: [],
    responseTimeStats: {
      average: 0,
      minimum: 0,
      maximum: 0
    }
  });
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [recentActivityItems, setRecentActivityItems] = useState<RecentActivity[]>([]);
  const activityPageSize = 10;

  const [providerOptions, setProviderOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedEndpointProviderId, setSelectedEndpointProviderId] = useState('');
  const [endpointProviderQuery, setEndpointProviderQuery] = useState('');
  const [topEndpoints, setTopEndpoints] = useState<TopEndpoint[]>([]);
  const [topEndpointsLoading, setTopEndpointsLoading] = useState(false);

  useEffect(() => {
    loadDashboardData();
    fetchProviderOptions();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setLoadingProgress(0);
    setLoadingMessage('Loading dashboard...');

    try {
      setLoadingProgress(30);
      setLoadingMessage('Loading overview data...');
      await fetchDashboardData();
      setLoadingProgress(60);

      setLoadingMessage('Loading analytics data...');
      await fetchAnalyticsData();
      setLoadingProgress(100);

      setTimeout(() => {
        setLoading(false);
      }, 100);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard/overview');
      if (response.data.success) {
        setDashboardData(response.data.data);
        setTopEndpoints(response.data.data.topEndpoints || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchProviderOptions = async () => {
    try {
      const providers = await apiProviderService.getAPIProviders();
      setProviderOptions(
        providers
          .filter((p) => p.id)
          .map((p) => ({ id: p.id as string, name: p.name }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (error) {
      console.error('Error fetching provider options:', error);
    }
  };

  const fetchTopEndpoints = async (providerId: string) => {
    setTopEndpointsLoading(true);
    try {
      const params = providerId ? `?providerId=${providerId}&limit=10` : '?limit=5';
      const response = await api.get(`/dashboard/top-endpoints${params}`);
      if (response.data.success) {
        setTopEndpoints(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching top endpoints:', error);
    } finally {
      setTopEndpointsLoading(false);
    }
  };

  const handleEndpointProviderFilterChange = (providerId: string) => {
    setSelectedEndpointProviderId(providerId);
    setEndpointProviderQuery('');
    fetchTopEndpoints(providerId);
  };

  const ALL_PROVIDERS_OPTION = { id: '', name: 'All API Providers' };
  const providerFilterOptions = useMemo(
    () => [ALL_PROVIDERS_OPTION, ...providerOptions],
    [providerOptions]
  );
  const filteredProviderFilterOptions = useMemo(() => {
    if (!endpointProviderQuery) return providerFilterOptions;
    const q = endpointProviderQuery.toLowerCase();
    return providerFilterOptions.filter((p) => p.name.toLowerCase().includes(q));
  }, [providerFilterOptions, endpointProviderQuery]);
  const selectedProviderFilterOption =
    providerFilterOptions.find((p) => p.id === selectedEndpointProviderId) || ALL_PROVIDERS_OPTION;

  const fetchRecentActivity = async (page: number) => {
    try {
      const response = await api.get(`/dashboard/activity?page=${page}&limit=${activityPageSize}`);
      if (response.data.success) {
        setRecentActivityItems(response.data.data.activities || []);
        setActivityTotal(response.data.data.pagination?.total || 0);
        setActivityPage(response.data.data.pagination?.page || page);
      }
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      const response = await api.get('/dashboard/analytics?period=30d');
      if (response.data.success) {
        setAnalyticsData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    }
  };

  // Calculate overview cards
  const overviewCards = [
    {
      title: 'Total API Keys',
      value: (dashboardData.overview.totalApiKeys || 0).toString(),
      subtext: `${dashboardData.overview.activeApiKeys || 0} Keys Active`,
      icon: Key,
      color: 'bg-blue-500',
    },
    {
      title: 'API Providers',
      value: (dashboardData.overview.totalProviders || 0).toString(),
      subtext: `${dashboardData.overview.activeProviders || 0} Providers Active`,
      icon: Users,
      color: 'bg-indigo-500',
    },
    {
      title: 'Total Endpoints',
      value: (dashboardData.overview.totalEndpoints || 0).toString(),
      subtext: `${dashboardData.overview.activeEndpoints || 0} Endpoints Active`,
      icon: Network,
      color: 'bg-cyan-500',
    },
    {
      title: 'Total Requests (All-Time)',
      value: (dashboardData.overview.totalRequestsAllTime || 0).toLocaleString(),
      subtext: `30d: ${(dashboardData.overview.totalRequests30d || 0).toLocaleString()} | 7d: ${(dashboardData.overview.totalRequests7d || 0).toLocaleString()}`,
      icon: Activity,
      color: 'bg-purple-500',
    },
    {
      title: 'Success Rate',
      value: `${(dashboardData.overview.successRate || 100).toFixed(1)}%`,
      subtext: `Avg Latency: ${dashboardData.overview.avgResponseTime || 0}ms`,
      icon: CheckCircle,
      color: 'bg-emerald-500',
    },
  ];

  // Process real chart data from analytics API
  const requestChartData = analyticsData.requestData.map(item => {
    const d = new Date(item.date);
    const label = isNaN(d.getTime()) ? String(item.date) : d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
    return {
      name: label,
      requests: item.count
    };
  });

  // Calculate status distribution from real data
  const totalRequests = analyticsData.statusCodeDistribution.reduce((sum, item) => sum + item.count, 0);
  const successRequests = analyticsData.statusCodeDistribution
    .filter(item => item.statusCode >= 200 && item.statusCode < 400)
    .reduce((sum, item) => sum + item.count, 0);
  const clientErrors = analyticsData.statusCodeDistribution
    .filter(item => item.statusCode >= 400 && item.statusCode < 500)
    .reduce((sum, item) => sum + item.count, 0);
  const serverErrors = analyticsData.statusCodeDistribution
    .filter(item => item.statusCode >= 500)
    .reduce((sum, item) => sum + item.count, 0);

  const statusData = [
    { 
      name: 'Success (2xx/3xx)', 
      value: totalRequests > 0 ? Number(((successRequests / totalRequests) * 100).toFixed(1)) : 100, 
      count: successRequests,
      color: '#10B981' 
    },
    { 
      name: 'Client Error (4xx)', 
      value: totalRequests > 0 ? Number(((clientErrors / totalRequests) * 100).toFixed(1)) : 0, 
      count: clientErrors,
      color: '#F59E0B' 
    },
    { 
      name: 'Server Error (5xx)', 
      value: totalRequests > 0 ? Number(((serverErrors / totalRequests) * 100).toFixed(1)) : 0, 
      count: serverErrors,
      color: '#EF4444' 
    },
  ];

  const CustomLineTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 border border-gray-700 text-white p-3 rounded-lg shadow-xl text-xs">
          <p className="font-medium text-gray-300">{label}</p>
          <p className="text-sm font-bold text-blue-400 mt-1">
            {payload[0].value.toLocaleString()} Requests
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-gray-900 border border-gray-700 text-white p-3 rounded-lg shadow-xl text-xs">
          <div className="flex items-center space-x-2 font-semibold text-gray-200">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: data.payload.color }} />
            <span>{data.name}</span>
          </div>
          <p className="text-sm font-bold mt-1" style={{ color: data.payload.color }}>
            {data.value.toFixed(1)}% <span className="text-xs font-normal text-gray-400">({(data.payload.count || 0).toLocaleString()} calls)</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Convert recent activity to display format
  const recentActivity = recentActivityItems.map((activity, index) => ({
    id: activity.id || index.toString(),
    type: 'api_request',
    message: `${activity.method} ${activity.endpoint} - ${activity.statusCode}`,
    time: new Date(activity.createdAt).toLocaleString(),
    status: activity.statusCode >= 200 && activity.statusCode < 300 ? 'success' : 
            activity.statusCode >= 400 && activity.statusCode < 500 ? 'warning' : 'error',
  }));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 transition-all duration-300">
        <div className="text-center w-full max-w-md">
          <div className="mb-6">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-4 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${loadingProgress}%` }}
            ></div>
          </div>
          
          {/* Progress Text */}
          <div className="mb-2">
            <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
              {loadingProgress}%
            </span>
          </div>
          
          {/* Loading Message */}
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
            {loadingMessage}
          </p>
          
          {/* Data Loading Info */}
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-500">
            <p>📊 Loading 171,874 API logs...</p>
            <p>⚡ Optimized with database indexes</p>
            {loadingProgress === 100 && (
              <p className="text-green-600 dark:text-green-400 mt-2 animate-pulse">
                ✨ Almost ready...
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Welcome back! Here's what's happening with your API Manager.
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => {
              loadDashboardData();
            }}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Refresh Data
          </button>
        </div>
      </motion.div>

      {/* Overview Cards */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6"
      >
        {overviewCards.map((item) => (
          <motion.div
            key={item.title}
            variants={itemVariants}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {item.title}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {item.value}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${item.color}`}>
                <item.icon className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="flex items-center mt-3 text-xs text-gray-500 dark:text-gray-400 font-mono">
              {item.subtext}
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Charts Section */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        {/* Request Trends */}
        <motion.div
          variants={itemVariants}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Request Trends (Daily)
            </h3>
            <LineChart className="w-5 h-5 text-gray-400" />
          </div>
          {requestChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <RechartsLineChart data={requestChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip content={<CustomLineTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="requests" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                />
              </RechartsLineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              No request data available
            </div>
          )}
        </motion.div>

        {/* Status Distribution */}
        <motion.div
          variants={itemVariants}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Response Status Distribution
            </h3>
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          {statusData.some(item => item.value > 0) ? (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center space-x-6 mt-4">
                {statusData.map((item) => (
                  <div key={item.name} className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {item.name}: {item.value.toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              No status data available
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Top Active API Keys & Top API Hits Section */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        {/* Top Active API Keys */}
        <motion.div
          variants={itemVariants}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Key className="w-5 h-5 text-blue-500 mr-2" />
              Top Active API Keys
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">By Request Volume</span>
          </div>

          <div className="space-y-4">
            {dashboardData.topApiKeys && dashboardData.topApiKeys.length > 0 ? (
              dashboardData.topApiKeys.map((keyItem) => {
                const maxReq = dashboardData.topApiKeys[0]?.requestCount || 1;
                const percentage = Math.max(5, Math.round((keyItem.requestCount / maxReq) * 100));

                return (
                  <div key={keyItem.id} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <div className="font-medium text-gray-900 dark:text-white flex items-center space-x-2">
                        <span>{keyItem.name}</span>
                        {keyItem.description && (
                          <span className="text-xs text-gray-400 font-normal">({keyItem.description})</span>
                        )}
                      </div>
                      <span className="font-mono text-xs font-semibold text-blue-600 dark:text-blue-400">
                        {keyItem.requestCount.toLocaleString()} requests
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">No API Key activity</div>
            )}
          </div>
        </motion.div>

        {/* Top API Providers (Paling Sering Dipanggil) */}
        <motion.div
          variants={itemVariants}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <TrendingUp className="w-5 h-5 text-purple-500 mr-2" />
              Top API Providers (Hits)
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">By Provider Hits</span>
          </div>

          <div className="space-y-4">
            {dashboardData.topProviders && dashboardData.topProviders.length > 0 ? (
              dashboardData.topProviders.map((prov) => {
                const maxHit = dashboardData.topProviders[0]?.hitCount || 1;
                const percentage = Math.max(5, Math.round((prov.hitCount / maxHit) * 100));

                return (
                  <div key={prov.id} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <div className="font-medium text-gray-900 dark:text-white flex items-center space-x-2 min-w-0 flex-1">
                        <span className="truncate">{prov.name}</span>
                      </div>
                      <span className="font-mono text-xs font-semibold text-purple-600 dark:text-purple-400 ml-2 whitespace-nowrap">
                        {prov.hitCount.toLocaleString()} hits
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">No Provider hit activity</div>
            )}
          </div>
        </motion.div>

        {/* Top Traffic per Endpoint */}
        <motion.div
          variants={itemVariants}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Flame className="w-5 h-5 text-orange-500 mr-2" />
              Top Traffic per Endpoint
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">By Hit Count</span>
          </div>

          <Combobox
            value={selectedProviderFilterOption}
            onChange={(option: { id: string; name: string }) => handleEndpointProviderFilterChange(option.id)}
          >
            <div className="relative mb-4">
              <div className="relative w-full">
                <Combobox.Input
                  className={`w-full text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white pl-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    selectedEndpointProviderId ? 'pr-16' : 'pr-9'
                  }`}
                  displayValue={(option: { id: string; name: string }) => option?.name || ''}
                  onChange={(e) => setEndpointProviderQuery(e.target.value)}
                  placeholder="Search API provider..."
                />
                {selectedEndpointProviderId && (
                  <button
                    type="button"
                    onClick={() => handleEndpointProviderFilterChange('')}
                    className="absolute inset-y-0 right-7 flex items-center pr-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    aria-label="Clear provider filter"
                  >
                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                </Combobox.Button>
              </div>
              {filteredProviderFilterOptions.length > 0 && (
                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 py-1 shadow-lg text-sm">
                  {filteredProviderFilterOptions.map((option) => (
                    <Combobox.Option
                      key={option.id || 'all'}
                      value={option}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-9 pr-3 ${
                          active ? 'bg-orange-500 text-white' : 'text-gray-900 dark:text-white'
                        }`
                      }
                    >
                      {({ selected, active }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-semibold' : ''}`}>
                            {option.name}
                          </span>
                          {selected && (
                            <span
                              className={`absolute inset-y-0 left-0 flex items-center pl-2.5 ${
                                active ? 'text-white' : 'text-orange-500'
                              }`}
                            >
                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              )}
            </div>
          </Combobox>

          <div className="space-y-4">
            {topEndpointsLoading ? (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">Loading...</div>
            ) : topEndpoints && topEndpoints.length > 0 ? (
              topEndpoints.map((ep) => {
                const maxHit = topEndpoints[0]?.hitCount || 1;
                const percentage = Math.max(5, Math.round((ep.hitCount / maxHit) * 100));

                return (
                  <div key={`${ep.providerId}-${ep.method}-${ep.endpoint}`} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <div className="font-medium text-gray-900 dark:text-white flex items-center space-x-2 min-w-0 flex-1">
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 font-mono">
                          {ep.method}
                        </span>
                        <span className="truncate font-mono text-xs" title={ep.endpoint}>{ep.endpoint}</span>
                      </div>
                      <span className="font-mono text-xs font-semibold text-orange-600 dark:text-orange-400 ml-2 whitespace-nowrap">
                        {ep.hitCount.toLocaleString()} hits
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 truncate">{ep.providerName}</div>
                    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">No endpoint hit activity</div>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
      >
        <div className="flex items-center justify-between p-6 pb-0 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Recent Activity (API Transactions)
          </h3>
          <Activity className="w-5 h-5 text-gray-400" />
        </div>
        <div className="space-y-3">
          {dashboardData.recentActivity && dashboardData.recentActivity.length > 0 ? (
            dashboardData.recentActivity.map((activity) => {
              const isSuccess = activity.statusCode >= 200 && activity.statusCode < 400;
              return (
                <div
                  key={activity.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600"
                >
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded font-mono ${
                      activity.method === 'GET' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' :
                      activity.method === 'POST' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' :
                      'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
                    }`}>
                      {activity.method}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                      isSuccess ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                    }`}>
                      {activity.statusCode}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm font-medium truncate text-gray-900 dark:text-white">
                        {activity.endpoint}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {activity.apiKeyName} • {activity.responseTime}ms
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-400 ml-3 whitespace-nowrap">
                    {new Date(activity.createdAt).toLocaleString('id-ID')}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No recent activity logs found
            </div>
          )}
        </div>
        <Pagination
          page={activityPage}
          total={activityTotal}
          pageSize={activityPageSize}
          onPageChange={(page) => fetchRecentActivity(page)}
        />
      </motion.div>
    </motion.div>
  );
} 